import { useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config/api';
import type { ApiResponse } from '../types/api';
import { saveAuth, type AuthResponse } from '../utils/auth';
import { wechatLoginLog } from '../utils/wechatLoginLog';

type PollData = {
  ready?: boolean;
  expired?: boolean;
  auth?: AuthResponse;
};

/**
 * PC 浏览器展示扫码后：用手机微信完成授权，本 hook 轮询后端直到拿到 token，再整页进入首页。
 */
export function useWeChatQrPollSession(
  pollingKey: string | null,
  opts?: { onExpired?: () => void },
): void {
  const expiredCbRef = useRef(opts?.onExpired);
  expiredCbRef.current = opts?.onExpired;
  const expiredNotifiedRef = useRef(false);

  useEffect(() => {
    expiredNotifiedRef.current = false;
    if (!pollingKey) return;

    let interval: ReturnType<typeof setInterval> | null = null;
    let stopped = false;

    const tick = async () => {
      if (stopped) return;
      try {
        const r = await fetch(`${API_BASE_URL}/auth/wechat/qr-poll?key=${encodeURIComponent(pollingKey)}`);
        const j = (await r.json()) as ApiResponse<PollData>;
        if (stopped) return;
        wechatLoginLog('wechat_qr_poll_tick', {
          httpOk: r.ok,
          httpStatus: r.status,
          success: Boolean(j.success),
          ready: Boolean(j.data?.ready),
          expired: Boolean(j.data?.expired),
        });
        if (j.success && j.data?.ready === true && j.data.auth) {
          stopped = true;
          if (interval) clearInterval(interval);
          saveAuth(j.data.auth);
          wechatLoginLog('wechat_qr_pc_login_ok', { userId: j.data.auth.user?.id });
          window.location.replace(`${window.location.origin}/`);
          return;
        }
        if (j.data?.expired === true) {
          stopped = true;
          if (interval) clearInterval(interval);
          wechatLoginLog('wechat_qr_poll_expired');
          if (!expiredNotifiedRef.current) {
            expiredNotifiedRef.current = true;
            expiredCbRef.current?.();
          }
        }
      } catch (e) {
        wechatLoginLog('wechat_qr_poll_err', { err: e instanceof Error ? e.message : String(e) });
      }
    };

    void tick();
    interval = setInterval(tick, 1600);
    return () => {
      stopped = true;
      if (interval) clearInterval(interval);
    };
  }, [pollingKey]);
}
